import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    linkedSignal,
    signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormField, form, min, required } from "@angular/forms/signals";
import { EventQuotaService } from "../../../services/event.quota.service";
import { EventQuota } from "../../../pojo/EventQuota";
import { EditEventQuota } from "./s25.edit.event.quotas.util";
import { Item } from "../../../pojo/Item";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { S25Util } from "../../../util/s25-util";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { MultiselectModelI } from "../../s25-multiselect/s25.multiselect.component";
import { s25Resource } from "../../../util/s25.resource";
import { S25EditableModule } from "../../s25-editable/s25.editable.module";
import { S25MultiselectModule } from "../../s25-multiselect/s25-multiselect.module";
import { S25ToggleButtonModule } from "../../s25-toggle-button/s25.toggle.button.module";
import { S25RadioModule } from "../../s25-radio/s25.radio.module";
import { S25SimpleCollapseModule } from "../../s25-simple-collapse/s25.simple.collapse.module";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { StateService } from "../../../services/state.service";
import { S25States } from "../../../services/state/state.map";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { SearchService } from "../../../services/search/search.service";
import { S25CheckboxModule } from "../../s25-checkbox/s25.checkbox.module";
import { S25TimepickerModule } from "../../s25-timepicker/s25.timepicker.module";
import { TimePickerModel } from "../../s25-timepicker/s25.timepicker";

const QUOTA_FORM_COPY = {
    summaryIntro: "Summary:",
    nameLabel: "Quota name",
    statusLabel: "Status",
    statusHelp: "Mark as inactive to temporarily disable this quota.",
    countForHeading: "Count Occurrences By",
    countForHelp: "Whether usage is totaled per user or per primary organization on the event.",
    organizationsLabel: "Organization scope",
    organizationsHelp:
        "Choose whether this quota applies to specific organizations or to organizations returned by a saved search.",
    orgScopeListLabel: "Specific organizations",
    orgScopeSearchLabel: "Organization search",
    orgScopeListHelp: "Applies when the event's primary organization is one of the organizations selected below.",
    orgScopeSearchHelp:
        "Applies when the event's primary organization matches the selected saved search. Private and public searches are both available.",
    orgScopeSearchPlaceholder: "Select an organization search",
    viewOrganizationSearchLabel: "View Organization Search",
    viewOrganizationSearchPrivateDisabledReason:
        "Only your own or public organization searches can be opened in a new tab.",
    rollingPeriodHeading: "Counting period",
    rollingPeriodHelp: "Defines how the period length is applied when counting occurrences toward the limits below.",
    rollingWindowHelp:
        "Limits apply in any consecutive sliding window of this length—for example, no more than X occurrences in any 30-day span, not only in the 30 days before today.",
    futureWindowHelp:
        "Same sliding rule as rolling, but only among upcoming reservations. The counting window never starts before today.",
    fixedDateRangeHelp:
        "Count all reservations between a fixed start and end date, such as a semester. Completed reservations count toward the limit.",
    forwardFromTodayHelp:
        "Count only active and upcoming reservations from today through the end date. Completed reservations do not count.",
    windowModeLabel: "Period direction",
    rollingPeriodModeLabel: "Rolling period",
    futurePeriodModeLabel: "Future only (rolling)",
    fixedDateRangeModeLabel: "Fixed date range",
    forwardFromTodayModeLabel: "Forward from today",
    rollingPeriodLengthLabel: "Period length",
    rollingPeriodLengthHelp: EventQuota.rollingPeriodLengthHelpText(),
    quotaStartDateLabel: "Start date",
    quotaEndDateLabel: "End date",
    quotaEndDateSpanHelp: EventQuota.fixedWindowSpanHelpText(),
    durationUnitLabel: "Unit",
    durationUnitWeekDefinition: "Monday through Sunday",
    durationUnitWeekHelp: "Weeks run Monday through Sunday.",
    limitsHeading: "Limits",
    limitsHelp: "Choose the maximum usage allowed within the counting period.",
    maxOccurrencesLabel: "Maximum occurrences",
    maxEventsLabel: "Maximum Events",
    maxBookedTimeLabel: "Maximum total booked time",
    maxBookedTimeHoursLabel: "Hours",
    maxBookedTimeMinutesLabel: "Minutes",
    scopeHeading: "Scope",
    whichReservationsLabel: "Which occurrences count",
    whichReservationsHelp:
        "Use single event only for embedded forms or guest users so prior occurrences are not counted.",
    allOccurrencesLabel: "All occurrences in period",
    singleEventLabel: "Single event only",
    countByEventLabel: "Count by event",
    countByEventHelp:
        "When enabled, quota periods and limits apply to the event's overall start and end dates. Multiple occurrences on the same event count once toward the event limit. Maximum booked time is not available for count-by-event quotas; use occurrence-based counting for time limits.",
    maxBookedTimeCountByEventHelp: "Maximum booked time applies only when counting by occurrence.",
    countByReservationLabel: "By occurrence",
    countByEventToggleLabel: "By event",
    locationsLabel: "Locations",
    locationsSearchHelp: "Type in the search box to find locations. The list stays empty until you search.",
    eventTypesLabel: "Event types (optional)",
    eventTypesHelp:
        "When selected, this quota applies only to events of these types. Leave empty to apply to all event types.",
    locationCountingLabel: "Location counting",
    locationCountingHelp:
        "When multiple locations are selected, choose whether each location has its own limit or locations share one combined limit.",
    perLocationLabel: "Each location separately",
    combinedLocationsLabel: "Combined across locations",
    includeRequestsLabel: "Include location requests",
    includeRequestsHelp:
        "When enabled, in-progress space assignment requests count toward the limit at each selected location, not only assigned spaces.",
    includeRequestsOffLabel: "Assigned locations only",
    includeRequestsOnLabel: "Include Requests",
    enforcementHeading: "Enforcement",
    securityGroupsLabel: "Security groups (optional)",
    securityGroupsHelp:
        "Applies only to users in these groups who do not have event quota override in functional level security. Leave empty to apply to all users without quota override.",
    exceededMessageLabel: "Message when quota is exceeded",
    exceededMessageHelp: "Shown to the user when this quota blocks creating or saving an event.",
    scheduleHeading: "Schedule (optional)",
    scheduleHelp:
        "Only occurrences on selected days and times count toward this quota. Use a separate quota for different limits on other days.",
    scheduleDaysLabel: "Days that count",
    scheduleWeekdaysPreset: "Weekdays",
    scheduleWeekendsPreset: "Weekends",
    scheduleAllDaysPreset: "All days",
    limitToTimeOfDayLabel: "Limit to time of day",
    limitToTimeOfDayHelp: "When enabled, only occurrences starting within this daily window count toward the limit.",
    allowedTimeStartLabel: "Start time",
    allowedTimeEndLabel: "End time",
} as const;

const DEFAULT_MULTISELECT: MultiselectModelI = {
    showResult: true,
    showMatching: true,
    filterFavorites: false,
};

function durationUnitLabel(durationType: string | undefined, duration: number | undefined): string {
    const count = duration ?? 0;
    const plural = count === 1 ? "" : "s";
    switch (durationType) {
        case "W":
            return `week${plural}`;
        case "M":
            return `month${plural}`;
        case "Y":
            return `year${plural}`;
        case "D":
        default:
            return `day${plural}`;
    }
}

function buildMultiselectBean(selectedItems: MultiselectModelI["selectedItems"]): MultiselectModelI {
    return {
        ...DEFAULT_MULTISELECT,
        selectedItems: selectedItems ? [...selectedItems] : [],
    };
}

const PUBLIC_ORGANIZATION_SEARCH_GROUP = "Public Searches";

function isPublicOrganizationSearch(search: EventQuota.QuotaScopeItem | S25ItemI | null | undefined): boolean {
    return search?.grp === PUBLIC_ORGANIZATION_SEARCH_GROUP;
}

function isOwnedOrganizationSearch(search: EventQuota.QuotaScopeItem | S25ItemI | null | undefined): boolean {
    return !!search?.isOwner;
}

function canOpenOrganizationSearch(search: EventQuota.QuotaScopeItem | S25ItemI | null | undefined): boolean {
    return isPublicOrganizationSearch(search) || isOwnedOrganizationSearch(search);
}

@TypeManagerDecorator("s25-ng-edit-event-quotas")
@Component({
    selector: "s25-ng-edit-event-quotas",
    host: { class: "quota-form" },
    standalone: true,
    imports: [
        CommonModule,
        FormField,
        S25EditableModule,
        S25MultiselectModule,
        S25ToggleButtonModule,
        S25RadioModule,
        S25SimpleCollapseModule,
        S25DropdownPaginatedModule,
        S25ButtonComponent,
        S25CheckboxModule,
        S25TimepickerModule,
    ],
    template: `
        @if (quotaSummary(); as summary) {
            <p class="quota-form__summary ngFinePrint" aria-live="polite">{{ summary }}</p>
        }

        <div class="quota-form__field quota-form__field--name">
            <label class="quota-form__label" for="quotaItemName">{{ copy.nameLabel }}</label>
            <s25-ng-editable-text
                fieldId="quotaItemName"
                [allowEmpty]="false"
                [alwaysEditing]="alwaysEditing()"
                [formField]="quotaForm.itemName"
            ></s25-ng-editable-text>
            @let itemNameField = quotaForm.itemName();
            @if (showFieldErrors(itemNameField)) {
                <span class="field-error">{{ itemNameField.errors()[0].message }}</span>
            }
        </div>
        <div class="quota-form__field">
            <label id="quotaStatusLabel" class="quota-form__label">{{ copy.statusLabel }}</label>
            <s25-toggle-button
                [formField]="quotaForm.active"
                [description]="copy.statusHelp"
                [falseLabel]="'Inactive'"
                [trueLabel]="'Active'"
                [toggleLabel]="copy.statusLabel"
            ></s25-toggle-button>
        </div>

        <s25-simple-collapse
            class="quota-form__collapse"
            [header]="copy.countForHeading"
            [titleText]="copy.countForHeading"
            [expanded]="true"
        >
            <div class="quota-form__field">
                <label id="quotaCountForLabel" class="quota-form__label">{{ copy.countForHeading }}</label>
                <p class="quota-form__help ngFinePrint">{{ copy.countForHelp }}</p>
                @if (quotaTypeReadOnly()) {
                    <p>{{ baselineDetailed().type?.itemName }}</p>
                } @else {
                    <div class="quota-form__radio-row" role="radiogroup" aria-labelledby="quotaCountForLabel">
                        <s25-ng-radio
                            [radioGroupName]="'quotaType'"
                            [formField]="quotaForm.quotaTypeItemId"
                            [radioValue]="quotaTypeUserItemId"
                            [ariaLabel]="copy.countForHeading"
                            (valueChange)="onQuotaTypeChange()"
                        >
                            User
                        </s25-ng-radio>
                        <s25-ng-radio
                            [radioGroupName]="'quotaType'"
                            [formField]="quotaForm.quotaTypeItemId"
                            [radioValue]="quotaTypeOrganizationItemId"
                            [ariaLabel]="copy.countForHeading"
                            (valueChange)="onQuotaTypeChange()"
                        >
                            Organization
                        </s25-ng-radio>
                    </div>
                }
            </div>
            @if (isOrganizationQuota()) {
                <div class="quota-form__field c-margin-top--half">
                    <label id="quotaOrganizationsLabel" class="quota-form__label">{{ copy.organizationsLabel }}</label>
                    <p class="quota-form__help ngFinePrint">{{ copy.organizationsHelp }}</p>

                    <div class="quota-form__radio-row " role="radiogroup" aria-labelledby="quotaOrganizationsLabel">
                        <s25-ng-radio
                            [radioGroupName]="'orgScopeMode'"
                            [formField]="quotaForm.orgScopeMode"
                            [radioValue]="orgScopeModeList"
                            [ariaLabel]="copy.orgScopeListLabel"
                        >
                            {{ copy.orgScopeListLabel }}
                        </s25-ng-radio>
                        <s25-ng-radio
                            [radioGroupName]="'orgScopeMode'"
                            [formField]="quotaForm.orgScopeMode"
                            [radioValue]="orgScopeModeSearch"
                            [ariaLabel]="copy.orgScopeSearchLabel"
                        >
                            {{ copy.orgScopeSearchLabel }}
                        </s25-ng-radio>
                    </div>
                </div>
                @if (isOrgListScope()) {
                    <div class="quota-form__field c-margin-top--half">
                        <p class="quota-form__help ngFinePrint">{{ copy.orgScopeListHelp }}</p>
                        <s25-ng-multiselect-search-criteria
                            [type]="'organizations'"
                            [modelBean]="organizationsBean()"
                            [formField]="quotaForm.organizations"
                            [showResults]="true"
                            [popoverOnBody]="true"
                            [popoverPlacement]="'right'"
                            aria-labelledby="quotaOrganizationsLabel"
                        ></s25-ng-multiselect-search-criteria>
                        @let organizationsField = quotaForm.organizations();
                        @if (showFieldErrors(organizationsField)) {
                            <span class="field-error">{{ organizationsField.errors()[0].message }}</span>
                        }
                    </div>
                } @else {
                    <div class="quota-form__field c-margin-top--half quota-form__field--org-search">
                        <p class="quota-form__help ngFinePrint">{{ copy.orgScopeSearchHelp }}</p>
                        <div class="quota-form__org-search-row">
                            <s25-ng-search-dropdown
                                [type]="'organization'"
                                [itemTypeId]="itemIds.Organization"
                                [allowNonQueryId]="false"
                                [placeholder]="copy.orgScopeSearchPlaceholder"
                                [formField]="quotaForm.organizationSearch"
                            ></s25-ng-search-dropdown>
                            @if (selectedOrganizationSearchId()) {
                                <s25-ng-button
                                    [type]="'outline'"
                                    [buttonText]="copy.viewOrganizationSearchLabel"
                                    [disabled]="!canViewOrganizationSearch()"
                                    [disabledReason]="viewOrganizationSearchDisabledReason()"
                                    (clicked)="openOrganizationSearch()"
                                />
                            }
                        </div>
                        @let organizationSearchField = quotaForm.organizationSearch();
                        @if (showFieldErrors(organizationSearchField)) {
                            <span class="field-error">{{ organizationSearchField.errors()[0].message }}</span>
                        }
                    </div>
                }
            }
        </s25-simple-collapse>

        <s25-simple-collapse
            class="quota-form__collapse"
            [header]="copy.rollingPeriodHeading"
            [titleText]="copy.rollingPeriodHeading"
            [expanded]="true"
        >
            <div class="quota-form__field">
                <label id="quotaWindowModeLabel" class="quota-form__label">{{ copy.windowModeLabel }}</label>
                <p class="quota-form__help ngFinePrint">{{ copy.rollingPeriodHelp }}</p>
                <div
                    class="quota-form__radio-row quota-form__radio-row--stacked"
                    role="radiogroup"
                    aria-labelledby="quotaWindowModeLabel"
                >
                    <s25-ng-radio
                        [radioGroupName]="'windowMode'"
                        [formField]="quotaForm.windowMode"
                        [radioValue]="windowModeRolling"
                        [ariaLabel]="copy.rollingPeriodModeLabel"
                    >
                        {{ copy.rollingPeriodModeLabel }}
                    </s25-ng-radio>
                    <p class="quota-form__help ngFinePrint quota-form__help--nested">{{ copy.rollingWindowHelp }}</p>
                    <s25-ng-radio
                        [radioGroupName]="'windowMode'"
                        [formField]="quotaForm.windowMode"
                        [radioValue]="windowModeFuture"
                        [ariaLabel]="copy.futurePeriodModeLabel"
                    >
                        {{ copy.futurePeriodModeLabel }}
                    </s25-ng-radio>
                    <p class="quota-form__help ngFinePrint quota-form__help--nested">{{ copy.futureWindowHelp }}</p>
                    <s25-ng-radio
                        [radioGroupName]="'windowMode'"
                        [formField]="quotaForm.windowMode"
                        [radioValue]="windowModeFixed"
                        [ariaLabel]="copy.fixedDateRangeModeLabel"
                    >
                        {{ copy.fixedDateRangeModeLabel }}
                    </s25-ng-radio>
                    <p class="quota-form__help ngFinePrint quota-form__help--nested">{{ copy.fixedDateRangeHelp }}</p>
                    <s25-ng-radio
                        [radioGroupName]="'windowMode'"
                        [formField]="quotaForm.windowMode"
                        [radioValue]="windowModeForwardToday"
                        [ariaLabel]="copy.forwardFromTodayModeLabel"
                    >
                        {{ copy.forwardFromTodayModeLabel }}
                    </s25-ng-radio>
                    <p class="quota-form__help ngFinePrint quota-form__help--nested">{{ copy.forwardFromTodayHelp }}</p>
                </div>
            </div>
            @if (showDurationFields()) {
                <div class="quota-form__field">
                    <label class="quota-form__label ngBold" for="quotaRollingPeriodDuration">
                        {{ copy.rollingPeriodLengthLabel }}
                        <s25-ng-editable-number
                            fieldId="quotaRollingPeriodDuration"
                            [ariaLabel]="copy.rollingPeriodLengthLabel"
                            [allowEmpty]="false"
                            [alwaysEditing]="alwaysEditing()"
                            [formField]="quotaForm.duration"
                        ></s25-ng-editable-number>
                    </label>
                    @let durationField = quotaForm.duration();
                    @if (showFieldErrors(durationField)) {
                        <span class="field-error">{{ durationField.errors()[0].message }}</span>
                    }
                    <p class="quota-form__help ngFinePrint">{{ copy.rollingPeriodLengthHelp }}</p>
                    <fieldset class="quota-form__fieldset c-margin-top--quarter">
                        <legend class="quota-form__label ngBold">{{ copy.durationUnitLabel }}</legend>
                        <div class="quota-form__radio-row" role="radiogroup">
                            <s25-ng-radio
                                [radioGroupName]="'durationUnit'"
                                [formField]="quotaForm.durationType"
                                [radioValue]="durationUnitDay"
                                [ariaLabel]="copy.durationUnitLabel"
                            >
                                Days
                            </s25-ng-radio>
                            <s25-ng-radio
                                [radioGroupName]="'durationUnit'"
                                [formField]="quotaForm.durationType"
                                [radioValue]="durationUnitWeek"
                                [ariaLabel]="copy.durationUnitLabel"
                            >
                                Weeks
                            </s25-ng-radio>
                            <s25-ng-radio
                                [radioGroupName]="'durationUnit'"
                                [formField]="quotaForm.durationType"
                                [radioValue]="durationUnitMonth"
                                [ariaLabel]="copy.durationUnitLabel"
                            >
                                Months
                            </s25-ng-radio>
                            <s25-ng-radio
                                [radioGroupName]="'durationUnit'"
                                [formField]="quotaForm.durationType"
                                [radioValue]="durationUnitYear"
                                [ariaLabel]="copy.durationUnitLabel"
                            >
                                Years
                            </s25-ng-radio>
                        </div>
                    </fieldset>
                    @if (quotaFormModel().durationType === durationUnitWeek) {
                        <p class="quota-form__help ngFinePrint">{{ copy.durationUnitWeekHelp }}</p>
                    }
                </div>
            }
            @if (showFixedDateRange()) {
                <div class="quota-form__field">
                    <label class="quota-form__label" for="quotaStartDate">{{ copy.quotaStartDateLabel }}</label>
                    <s25-ng-editable-date
                        class="quota-form__date-input"
                        fieldId="quotaStartDate"
                        [inputId]="'quotaStartDate'"
                        [allowEmpty]="false"
                        [alwaysEditing]="alwaysEditing()"
                        [hasIcon]="false"
                        [formField]="quotaForm.quotaStartDt"
                    />
                    @let quotaStartField = quotaForm.quotaStartDt();
                    @if (showFieldErrors(quotaStartField)) {
                        <span class="field-error">{{ quotaStartField.errors()[0].message }}</span>
                    }
                </div>
            }
            @if (showFixedDateRange() || showEndDateOnly()) {
                <div class="quota-form__field">
                    <label class="quota-form__label" for="quotaEndDate">{{ copy.quotaEndDateLabel }}</label>
                    <s25-ng-editable-date
                        class="quota-form__date-input"
                        fieldId="quotaEndDate"
                        [inputId]="'quotaEndDate'"
                        [allowEmpty]="false"
                        [alwaysEditing]="alwaysEditing()"
                        [hasIcon]="false"
                        [minDate]="quotaEndMinDate()"
                        [formField]="quotaForm.quotaEndDt"
                    />
                    @let quotaEndField = quotaForm.quotaEndDt();
                    @if (showFieldErrors(quotaEndField)) {
                        <span class="field-error">{{ quotaEndField.errors()[0].message }}</span>
                    }
                    @if (showFixedDateRange() || showEndDateOnly()) {
                        <p class="quota-form__help ngFinePrint">{{ copy.quotaEndDateSpanHelp }}</p>
                    }
                </div>
            }
        </s25-simple-collapse>

        <s25-simple-collapse
            class="quota-form__collapse"
            [header]="copy.limitsHeading"
            [titleText]="copy.limitsHeading"
            [expanded]="true"
        >
            <div class="quota-form__field">
                <label id="quotaCountByEventLabel" class="quota-form__label">{{ copy.countByEventLabel }}</label>
                <p class="quota-form__help ngFinePrint">{{ copy.countByEventHelp }}</p>
                <s25-toggle-button
                    [formField]="quotaForm.countByEvent"
                    [description]="copy.countByEventHelp"
                    [falseLabel]="copy.countByReservationLabel"
                    [trueLabel]="copy.countByEventToggleLabel"
                    [toggleLabel]="copy.countByEventLabel"
                ></s25-toggle-button>
            </div>
            <p class="quota-form__help ngFinePrint">{{ copy.limitsHelp }}</p>
            <div class="quota-form__row">
                <div class="quota-form__field">
                    <label class="quota-form__label ngBold" for="quotaMaxOccurrences">
                        {{ maxQuantityLabel() }}
                        <s25-ng-editable-number
                            fieldId="quotaMaxOccurrences"
                            [ariaLabel]="maxQuantityLabel()"
                            [allowEmpty]="true"
                            [alwaysEditing]="alwaysEditing()"
                            [formField]="quotaForm.quantity"
                        ></s25-ng-editable-number>
                    </label>
                </div>
                <div class="quota-form__field">
                    <span id="quotaMaxBookedTimeLabel" class="quota-form__label">{{ copy.maxBookedTimeLabel }}</span>
                    @if (quotaFormModel().countByEvent) {
                        <p class="quota-form__help ngFinePrint">{{ copy.maxBookedTimeCountByEventHelp }}</p>
                    } @else {
                        <div class="quota-form__time-row" role="group" aria-labelledby="quotaMaxBookedTimeLabel">
                            <label class="quota-form__time-part" for="quotaMaxTimeHours">
                                <span class="quota-form__time-part-label">{{ copy.maxBookedTimeHoursLabel }}</span>
                                <s25-ng-editable-number
                                    fieldId="quotaMaxTimeHours"
                                    [ariaLabel]="copy.maxBookedTimeHoursLabel"
                                    [allowEmpty]="true"
                                    [alwaysEditing]="alwaysEditing()"
                                    [formField]="quotaForm.maxTimeHours"
                                ></s25-ng-editable-number>
                            </label>
                            <label class="quota-form__time-part" for="quotaMaxTimeMinutes">
                                <span class="quota-form__time-part-label">{{ copy.maxBookedTimeMinutesLabel }}</span>
                                <s25-ng-editable-number
                                    fieldId="quotaMaxTimeMinutes"
                                    [ariaLabel]="copy.maxBookedTimeMinutesLabel"
                                    [allowEmpty]="true"
                                    [alwaysEditing]="alwaysEditing()"
                                    [formField]="quotaForm.maxTimeMinutes"
                                ></s25-ng-editable-number>
                            </label>
                        </div>
                    }
                </div>
            </div>
            @if (showLimitsErrors()) {
                @for (error of limitsErrorMessages(); track error.message) {
                    <span class="field-error" role="alert">{{ error.message }}</span>
                }
            }
        </s25-simple-collapse>

        <s25-simple-collapse
            class="quota-form__collapse"
            [header]="copy.scheduleHeading"
            [titleText]="copy.scheduleHeading"
            [expanded]="true"
        >
            <p class="quota-form__help ngFinePrint">{{ copy.scheduleHelp }}</p>
            <div class="quota-form__field">
                <span id="quotaScheduleDaysLabel" class="quota-form__label">{{ copy.scheduleDaysLabel }}</span>
                <div class="quota-form__day-actions">
                    <button type="button" class="quota-form__link-button" (click)="applyAllDaysPreset()">
                        {{ copy.scheduleAllDaysPreset }}
                    </button>
                    <button type="button" class="quota-form__link-button" (click)="applyWeekdaysPreset()">
                        {{ copy.scheduleWeekdaysPreset }}
                    </button>
                    <button type="button" class="quota-form__link-button" (click)="applyWeekendsPreset()">
                        {{ copy.scheduleWeekendsPreset }}
                    </button>
                </div>
                <div class="quota-form__day-row" role="group" aria-labelledby="quotaScheduleDaysLabel">
                    @for (dayLabel of dayLabels; track dayLabel; let dayIndex = $index) {
                        <s25-ng-checkbox
                            class="quota-form__day-checkbox"
                            [labelId]="'quota-schedule-day-' + dayIndex"
                            [value]="quotaFormModel().daySelection[dayIndex]"
                            (valueChange)="setScheduleDay(dayIndex, $event)"
                        >
                            {{ dayLabel }}
                        </s25-ng-checkbox>
                    }
                </div>
                @let dayField = quotaForm.daySelection();
                @if (showFieldErrors(dayField)) {
                    <span class="field-error">{{ dayField.errors()[0].message }}</span>
                }
            </div>
            <div class="quota-form__field">
                <s25-toggle-button
                    [formField]="quotaForm.limitToTimeOfDay"
                    [description]="copy.limitToTimeOfDayHelp"
                    [falseLabel]="'All day'"
                    [trueLabel]="copy.limitToTimeOfDayLabel"
                    [toggleLabel]="copy.limitToTimeOfDayLabel"
                ></s25-toggle-button>
                @if (quotaFormModel().limitToTimeOfDay) {
                    <div class="quota-form__time-row" role="group" [attr.aria-label]="copy.limitToTimeOfDayLabel">
                        <div class="quota-form__time-part">
                            <span class="quota-form__time-part-label" id="quotaAllowedTimeStartLabel">{{
                                copy.allowedTimeStartLabel
                            }}</span>
                            <s25-timepicker
                                inputId="quotaAllowedTimeStart"
                                [inputLabel]="copy.allowedTimeStartLabel"
                                [modelBean]="allowedTimeStartPickerBean"
                                [modelValue]="scheduleTimeForPicker(quotaFormModel().allowedTimeStart)"
                                (modelValueChange)="onAllowedTimeStartChange($event)"
                            ></s25-timepicker>
                        </div>
                        <div class="quota-form__time-part">
                            <span class="quota-form__time-part-label" id="quotaAllowedTimeEndLabel">{{
                                copy.allowedTimeEndLabel
                            }}</span>
                            <s25-timepicker
                                inputId="quotaAllowedTimeEnd"
                                [inputLabel]="copy.allowedTimeEndLabel"
                                [modelBean]="allowedTimeEndPickerBean"
                                [modelValue]="scheduleTimeForPicker(quotaFormModel().allowedTimeEnd)"
                                (modelValueChange)="onAllowedTimeEndChange($event)"
                            ></s25-timepicker>
                        </div>
                    </div>
                    @if (showScheduleTimeErrors()) {
                        @for (error of scheduleTimeErrorMessages(); track error.message) {
                            <span class="field-error" role="alert">{{ error.message }}</span>
                        }
                    }
                }
            </div>
        </s25-simple-collapse>

        <s25-simple-collapse
            class="quota-form__collapse"
            [header]="copy.scopeHeading"
            [titleText]="copy.scopeHeading"
            [expanded]="true"
        >
            <div class="quota-form__field">
                <label id="quotaWhichReservationsLabel" class="quota-form__label">{{
                    copy.whichReservationsLabel
                }}</label>
                <p class="quota-form__help ngFinePrint">{{ copy.whichReservationsHelp }}</p>
                <s25-toggle-button
                    [formField]="quotaForm.skipExisting"
                    [description]="copy.whichReservationsHelp"
                    [falseLabel]="copy.allOccurrencesLabel"
                    [trueLabel]="copy.singleEventLabel"
                    [toggleLabel]="copy.whichReservationsLabel"
                ></s25-toggle-button>
            </div>
            <div class="quota-form__field">
                <label id="quotaLocationsLabel" class="quota-form__label">{{ copy.locationsLabel }}</label>
                <p class="quota-form__help ngFinePrint">{{ copy.locationsSearchHelp }}</p>
                <s25-ng-multiselect-search-criteria
                    [type]="'locations'"
                    [modelBean]="locationsBean()"
                    [formField]="quotaForm.locations"
                    [showResults]="true"
                    [popoverOnBody]="true"
                    [popoverPlacement]="'right'"
                    aria-labelledby="quotaLocationsLabel"
                ></s25-ng-multiselect-search-criteria>
            </div>

            @if (showIncludeRequests()) {
                <div class="quota-form__field">
                    <label id="quotaIncludeRequestsLabel" class="quota-form__label">{{
                        copy.includeRequestsLabel
                    }}</label>
                    <p class="quota-form__help ngFinePrint">{{ copy.includeRequestsHelp }}</p>
                    <s25-toggle-button
                        [formField]="quotaForm.includeRequests"
                        [description]="copy.includeRequestsHelp"
                        [falseLabel]="copy.includeRequestsOffLabel"
                        [trueLabel]="copy.includeRequestsOnLabel"
                        [toggleLabel]="copy.includeRequestsLabel"
                    ></s25-toggle-button>
                </div>
            }
            @if (showLocationGrouping()) {
                <div class="quota-form__field">
                    <label id="quotaLocationCountingLabel" class="quota-form__label">{{
                        copy.locationCountingLabel
                    }}</label>
                    <p class="quota-form__help ngFinePrint">{{ copy.locationCountingHelp }}</p>
                    <div class="quota-form__radio-row" role="radiogroup" aria-labelledby="quotaLocationCountingLabel">
                        <s25-ng-radio
                            [radioGroupName]="'groupBy'"
                            [formField]="quotaForm.groupItems"
                            [radioValue]="false"
                            [ariaLabel]="copy.locationCountingLabel"
                        >
                            {{ copy.perLocationLabel }}
                        </s25-ng-radio>
                        <s25-ng-radio
                            [radioGroupName]="'groupBy'"
                            [formField]="quotaForm.groupItems"
                            [radioValue]="true"
                            [ariaLabel]="copy.locationCountingLabel"
                        >
                            {{ copy.combinedLocationsLabel }}
                        </s25-ng-radio>
                    </div>
                </div>
            }
            <div class="quota-form__field c-margin-top--single">
                <label id="quotaEventTypesLabel" class="quota-form__label">{{ copy.eventTypesLabel }}</label>
                <p class="quota-form__help ngFinePrint">{{ copy.eventTypesHelp }}</p>
                <s25-ng-multiselect-search-criteria
                    [type]="'eventTypes'"
                    [modelBean]="eventTypesBean()"
                    [formField]="quotaForm.eventTypes"
                    [showResults]="true"
                    [popoverOnBody]="true"
                    [popoverPlacement]="'right'"
                    aria-labelledby="quotaEventTypesLabel"
                ></s25-ng-multiselect-search-criteria>
            </div>
        </s25-simple-collapse>

        <s25-simple-collapse
            class="quota-form__collapse"
            [header]="copy.enforcementHeading"
            [titleText]="copy.enforcementHeading"
            [expanded]="true"
        >
            <div class="quota-form__field">
                <label id="quotaSecurityGroupsLabel" class="quota-form__label">{{ copy.securityGroupsLabel }}</label>
                <p class="quota-form__help ngFinePrint">{{ copy.securityGroupsHelp }}</p>
                <s25-ng-multiselect-search-criteria
                    [type]="'securityGroups'"
                    [modelBean]="secGroupsBean()"
                    [formField]="quotaForm.secGroups"
                    [showResults]="true"
                    [popoverOnBody]="true"
                    [popoverPlacement]="'right'"
                    aria-labelledby="quotaSecurityGroupsLabel"
                ></s25-ng-multiselect-search-criteria>
            </div>
            <div class="quota-form__field">
                <label class="quota-form__label" for="quotaExceededMessage">{{ copy.exceededMessageLabel }}</label>
                <p class="quota-form__help ngFinePrint">{{ copy.exceededMessageHelp }}</p>
                <textarea
                    id="quotaExceededMessage"
                    [formField]="quotaForm.comment"
                    rows="5"
                    class="quota-form__textarea c-input"
                ></textarea>
            </div>
        </s25-simple-collapse>
    `,
    styles: `
        :host {
            display: flex;
            flex-direction: column;
            gap: 1.25rem;
        }

        .quota-form__summary {
            margin: 0;
            padding: 0.75rem 1rem;
            border-radius: var(--border-radius);
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
        }

        .quota-form__collapse {
            display: block;
            border: 1px solid var(--color--border);
            border-radius: var(--border-radius);
            background: var(--color--surface-bg);
        }

        :host ::ng-deep .quota-form__collapse .content-wrapper {
            padding: 1rem 1.25rem 1.25rem;
            border-top-color: var(--color--border);
        }

        .quota-form__field {
            display: block;
        }

        .quota-form__field:last-child {
            margin-bottom: 0;
        }

        .quota-form__field--name ::ng-deep s25-ng-editable-text .c-input {
            min-width: 18rem;
            width: 100%;
            max-width: 100%;
        }

        .quota-form__label {
            display: block;
            font-weight: 600;
            margin-bottom: 0.25rem;
        }

        .quota-form__label > s25-ng-editable-number,
        .quota-form__label > s25-ng-editable-text {
            display: block;
            margin-top: 0.25rem;
        }

        .quota-form__time-row {
            display: flex;
            flex-wrap: wrap;
            gap: 1rem;
            margin-top: 0.25rem;
        }

        .quota-form__time-part {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
            min-width: 6rem;
        }

        .quota-form__time-part-label {
            font-weight: 600;
        }

        .quota-form__time-part > s25-ng-editable-number {
            display: block;
        }

        .quota-form__fieldset {
            border: 0;
            margin: 0;
            min-width: 0;
            padding: 0;
        }

        .quota-form__fieldset legend.quota-form__label {
            float: none;
            width: auto;
            font-size: inherit;
            line-height: inherit;
            font-weight: 600;
            margin-bottom: 0.25rem;
            padding: 0;
        }

        .quota-form__help {
            margin: 0 0 0.5rem;
        }

        .field-error {
            display: block;
            margin-top: 0.35rem;
            color: var(--color--danger);
            font-size: 0.85rem;
        }

        .quota-form__row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1.25rem;
        }

        .quota-form__radio-row {
            display: flex;
            flex-wrap: wrap;
            gap: 1em;
        }

        .quota-form__radio-row--stacked {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.35rem;
        }

        .quota-form__help--nested {
            margin: 0 0 0.75rem 1.5rem;
        }

        .quota-form__textarea {
            display: block;
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
        }

        .quota-form__date-input {
            display: block;
            max-width: 16rem;
        }

        :host ::ng-deep .quota-form__date-input .editable-input {
            width: 100%;
            max-width: 16rem;
        }

        :host ::ng-deep .s25-multiselect-popup-container {
            --multiselect--preferred-width: 50vw;
            min-width: 28rem;
        }

        :host ::ng-deep .s25-multiselect-popup .s25-multiselect-columns-container {
            min-height: 8rem;
            max-height: 35vh;
        }

        .quota-form__org-search-row {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 0.5rem;
        }

        :host ::ng-deep .quota-form__org-search-row s25-ng-search-dropdown {
            flex: 1 1 16rem;
            min-width: 16rem;
            max-width: 25em;
            display: block;
        }

        .quota-form__org-search-row s25-ng-button {
            flex: 0 0 auto;
        }

        .quota-form__day-actions {
            display: flex;
            flex-wrap: wrap;
            gap: 0.75rem;
            margin-bottom: 0.5rem;
        }

        .quota-form__link-button {
            background: none;
            border: none;
            padding: 0;
            color: var(--color--link-text);
            cursor: pointer;
            text-decoration: underline;
        }

        .quota-form__day-row {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem 1rem;
        }

        .quota-form__day-checkbox {
            display: inline-block;
        }

        .quota-form__time-part s25-timepicker {
            display: block;
            max-width: 8rem;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class S25EditEventQuotasComponent {
    private readonly host = inject(ElementRef<HTMLElement>);

    readonly copy = QUOTA_FORM_COPY;

    readonly model = input.required<EventQuota.Detailed>();
    readonly alwaysEditing = input(true);
    readonly isNew = input(true);

    readonly durationUnitDay = "D";
    readonly durationUnitWeek = "W";
    readonly durationUnitMonth = "M";
    readonly durationUnitYear = "Y";
    readonly windowModeRolling = EventQuota.DEFAULT_WINDOW_MODE;
    readonly windowModeFuture: EventQuota.WindowMode = "future";
    readonly windowModeFixed: EventQuota.WindowMode = "fixed";
    readonly windowModeForwardToday: EventQuota.WindowMode = "today";
    readonly quotaTypeUserItemId = String(EventQuota.TypeIds.User);
    readonly quotaTypeOrganizationItemId = String(Item.Ids.Organization);
    readonly orgScopeModeList: EventQuota.OrgScopeMode = "list";
    readonly orgScopeModeSearch: EventQuota.OrgScopeMode = "search";
    readonly itemIds = Item.Ids;
    readonly dayLabels = EventQuota.DAY_LABELS;

    readonly allowedTimeStartPickerBean: TimePickerModel = { initEmpty: true };
    readonly allowedTimeEndPickerBean: TimePickerModel = { initEmpty: true };

    readonly baselineDetailed = linkedSignal<EventQuota.Detailed>(() => {
        const seed = S25Util.deepCopy(this.model());
        EditEventQuota.normalizeEventQuotaModel(seed);
        return seed;
    });

    readonly quotaFormModel = linkedSignal<EditEventQuota.FormModel>(() =>
        EditEventQuota.detailedToFormModel(S25Util.deepCopy(this.model())),
    );

    readonly originalName = linkedSignal(() => this.baselineDetailed().itemName ?? "");

    // Seed modelBean from baseline only — not quotaFormModel. Form edits via [formField] must not replace
    // modelBean or search-criteria init state (title, buttonText, expensiveSearch) is lost.
    readonly organizationsBean = linkedSignal(() => buildMultiselectBean(this.baselineDetailed().organizations));
    readonly locationsBean = linkedSignal(() => buildMultiselectBean(this.baselineDetailed().locations));
    readonly eventTypesBean = linkedSignal(() => buildMultiselectBean(this.baselineDetailed().eventTypes));
    readonly secGroupsBean = linkedSignal(() =>
        buildMultiselectBean(this.baselineDetailed().secGroups ?? this.baselineDetailed().securityGroups),
    );

    constructor() {
        effect(() => {
            const formModel = this.quotaFormModel();
            if (!formModel.countByEvent) {
                return;
            }
            if (!EventQuota.hasMeaningfulMaxTimeHoursMinutes(formModel.maxTimeHours, formModel.maxTimeMinutes)) {
                return;
            }
            this.quotaFormModel.update((current) => EditEventQuota.clearMaxBookedTimeWhenCountByEvent(current));
        });
    }

    readonly quotaForm = form(this.quotaFormModel, (schema) => {
        required(schema.itemName, { message: "Please enter a quota name" });
        EventQuota.Validators.itemName(schema.itemName);
        EventQuota.Validators.uniqueItemName(schema.itemName, {
            takenNames: () => this.takenNames(),
        });
        EventQuota.Validators.duration(schema.duration, schema.windowMode, schema.durationType);
        EventQuota.Validators.durationType(schema.durationType, schema.windowMode);
        EventQuota.Validators.windowMode(schema.windowMode);
        EventQuota.Validators.quotaStartDt(schema.quotaStartDt, schema.windowMode);
        EventQuota.Validators.quotaEndDt(schema.quotaEndDt, schema.windowMode, schema.quotaStartDt);
        EventQuota.Validators.requiresAtLeastOneLimit(
            schema.quantity,
            schema.maxTimeHours,
            schema.maxTimeMinutes,
            schema.countByEvent,
        );
        EventQuota.Validators.countByEventDisallowsMaxTime(
            schema.countByEvent,
            schema.maxTimeHours,
            schema.maxTimeMinutes,
        );
        min(schema.maxTimeHours, 0, { message: "Hours cannot be negative." });
        EventQuota.Validators.maxTimeMinutes(schema.maxTimeMinutes);
        EventQuota.Validators.organizationsWhenOrgType(
            schema.organizations,
            schema.quotaTypeItemId,
            schema.orgScopeMode,
            schema.organizationSearch,
        );
        EventQuota.Validators.daySelection(schema.daySelection);
        EventQuota.Validators.allowedTimeBand(schema.allowedTimeStart, schema.allowedTimeEnd, schema.limitToTimeOfDay);
    });

    readonly submitAttempted = signal(false);

    readonly allQuotas = s25Resource({ loader: () => EventQuotaService.getAll() });
    readonly takenNames = computed(() => {
        const names = new Set(this.allQuotas.value()?.map((quota) => quota.itemName ?? "") || []);
        if (!this.isNew()) {
            names.delete(this.originalName());
        }

        return names;
    });

    readonly isOrganizationQuota = computed(() =>
        EditEventQuota.isOrganizationQuotaType(this.quotaFormModel().quotaTypeItemId),
    );

    readonly isOrgListScope = computed(() => this.quotaFormModel().orgScopeMode === "list");

    readonly selectedOrganizationSearchId = computed(() => {
        const itemId = this.quotaFormModel().organizationSearch?.itemId;
        return itemId && Number.isFinite(itemId) ? itemId : undefined;
    });

    readonly organizationSearches = s25Resource({
        loader: (): Promise<S25ItemI[]> =>
            SearchService.getStrictSearches(Item.Ids.Organization, false).then((data) => data?.opt ?? []),
    });

    readonly canViewOrganizationSearch = computed(() => {
        const searchId = this.selectedOrganizationSearchId();
        if (!searchId) {
            return false;
        }

        const selectedSearch = this.quotaFormModel().organizationSearch;
        if (canOpenOrganizationSearch(selectedSearch)) {
            return true;
        }

        const searches = this.organizationSearches.value();
        if (!searches) {
            return false;
        }

        // Same query can appear in multiple groups (e.g. starred + all yours); any openable match counts.
        return searches.some(
            (search) => S25Util.toInt(search.itemId) === S25Util.toInt(searchId) && canOpenOrganizationSearch(search),
        );
    });

    readonly viewOrganizationSearchDisabledReason = computed(() =>
        this.selectedOrganizationSearchId() && !this.canViewOrganizationSearch()
            ? QUOTA_FORM_COPY.viewOrganizationSearchPrivateDisabledReason
            : undefined,
    );

    readonly quotaTypeReadOnly = computed(() => Boolean(this.baselineDetailed().type?.readOnly));

    readonly showLocationGrouping = computed(() => {
        const typeId = EventQuota.toQuotaTypeId(this.quotaFormModel().quotaTypeItemId);
        return typeId !== EventQuota.TypeIds.System;
    });

    readonly showIncludeRequests = computed(() => {
        const formModel = this.quotaFormModel();
        return (formModel.locations?.length ?? 0) > 0 || this.showLocationGrouping();
    });

    readonly maxQuantityLabel = computed(() =>
        this.quotaFormModel().countByEvent ? QUOTA_FORM_COPY.maxEventsLabel : QUOTA_FORM_COPY.maxOccurrencesLabel,
    );

    readonly showDurationFields = computed(() => EventQuota.usesDurationForWindow(this.quotaFormModel().windowMode));

    readonly showFixedDateRange = computed(
        () => EventQuota.normalizeWindowMode(this.quotaFormModel().windowMode) === "fixed",
    );

    readonly showEndDateOnly = computed(
        () => EventQuota.normalizeWindowMode(this.quotaFormModel().windowMode) === "today",
    );

    readonly quotaEndMinDate = computed(() => {
        if (!this.showFixedDateRange()) {
            return undefined;
        }
        const startDate = this.quotaFormModel().quotaStartDt;
        return startDate && !isNaN(startDate.getTime()) ? startDate : undefined;
    });

    readonly quotaSummary = computed(() => this.buildQuotaSummary(this.quotaFormModel()));

    readonly limitsErrors = computed(() => {
        const quantityErrors = this.quotaForm.quantity().errors();
        const maxTimeHoursErrors = this.quotaForm.maxTimeHours().errors();
        const maxTimeMinutesErrors = this.quotaForm.maxTimeMinutes().errors();
        return [...quantityErrors, ...maxTimeHoursErrors, ...maxTimeMinutesErrors];
    });

    readonly limitsErrorMessages = computed(() => {
        const seenMessages = new Set<string>();
        return this.limitsErrors().filter((error) => {
            if (seenMessages.has(error.message ?? "")) {
                return false;
            }
            seenMessages.add(error.message ?? "Unknown error");
            return true;
        });
    });

    showFieldErrors(field: { invalid: () => boolean; touched: () => boolean }): boolean {
        return (field.touched() || this.submitAttempted()) && field.invalid();
    }

    showLimitsErrors(): boolean {
        return this.submitAttempted() && this.limitsErrors().length > 0;
    }

    readonly scheduleTimeErrors = computed(() => {
        const startErrors = this.quotaForm.allowedTimeStart().errors();
        const endErrors = this.quotaForm.allowedTimeEnd().errors();
        const limitErrors = this.quotaForm.limitToTimeOfDay().errors();
        return [...startErrors, ...endErrors, ...limitErrors];
    });

    readonly scheduleTimeErrorMessages = computed(() => {
        const seenMessages = new Set<string>();
        return this.scheduleTimeErrors().filter((error) => {
            if (seenMessages.has(error.message ?? "")) {
                return false;
            }
            seenMessages.add(error.message ?? "Unknown error");
            return true;
        });
    });

    showScheduleTimeErrors(): boolean {
        return this.submitAttempted() && this.scheduleTimeErrors().length > 0;
    }

    setScheduleDay(dayIndex: number, checked: boolean): void {
        this.quotaFormModel.update((formModel) => {
            const daySelection = [...formModel.daySelection];
            daySelection[dayIndex] = checked;
            return {
                ...formModel,
                daySelection,
                dow: EventQuota.dowFromSelection(daySelection),
            };
        });
        this.quotaForm.daySelection().markAsTouched();
    }

    scheduleTimeForPicker(timeValue: string | null | undefined): Date | undefined {
        if (!timeValue?.trim()) {
            return undefined;
        }
        return S25Util.date.parseFuzzyTimeString(timeValue.trim()) ?? undefined;
    }

    onAllowedTimeStartChange(date: Date): void {
        this.patchAllowedScheduleTime("allowedTimeStart", date);
    }

    onAllowedTimeEndChange(date: Date): void {
        this.patchAllowedScheduleTime("allowedTimeEnd", date);
    }

    applyAllDaysPreset(): void {
        this.quotaFormModel.update((formModel) => ({
            ...formModel,
            daySelection: EventQuota.selectionFromDow(EventQuota.ALL_DOW) as boolean[],
            dow: EventQuota.ALL_DOW,
        }));
    }

    applyWeekdaysPreset(): void {
        this.setDaySelectionPreset([false, true, true, true, true, true, false]);
    }

    applyWeekendsPreset(): void {
        this.setDaySelectionPreset([true, false, false, false, false, false, true]);
    }

    openOrganizationSearch(): void {
        const searchId = this.selectedOrganizationSearchId();
        if (!searchId || !this.canViewOrganizationSearch()) {
            return;
        }

        void StateService.go(
            S25States.names.search,
            { viz: "list", subject: "organization", search: searchId },
            { source: "edit-event-quota-organization-search" },
            true,
        );
    }

    onQuotaTypeChange(): void {
        if (!EditEventQuota.isOrganizationQuotaType(this.quotaFormModel().quotaTypeItemId)) {
            this.quotaFormModel.update((formModel) => ({
                ...formModel,
                orgScopeMode: "list",
                organizations: [],
                organizationSearch: null,
            }));
        }
    }

    save = (): Promise<EventQuota.Detailed | false> => {
        this.submitAttempted.set(true);
        if (this.quotaForm().invalid()) {
            this.scrollToFirstValidationError();
            return Promise.resolve(false);
        }

        const detailed = EditEventQuota.formModelToDetailed(this.quotaFormModel(), this.baselineDetailed(), {
            isNew: this.isNew(),
        });
        return EventQuotaService.set([detailed]).then(
            () => detailed,
            (err) => {
                S25Util.showError(err);
                return false;
            },
        );
    };

    close = (_type: string): void => {
        // Cancel/save lifecycle is handled by the parent list + modal.
    };

    private scrollToFirstValidationError(): void {
        setTimeout(() => {
            this.host.nativeElement
                .querySelector(".field-error")
                ?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 0);
    }

    private setDaySelectionPreset(selected: boolean[]): void {
        this.quotaFormModel.update((formModel) => ({
            ...formModel,
            daySelection: [...selected],
            dow: EventQuota.dowFromSelection(selected),
        }));
        this.quotaForm.daySelection().markAsTouched();
    }

    private patchAllowedScheduleTime(
        formField: "allowedTimeStart" | "allowedTimeEnd",
        date: Date | null | undefined,
    ): void {
        const formatted = date ? S25Util.date.toTimeStrFromHours(date.getHours() + date.getMinutes() / 60, true) : null;
        this.quotaFormModel.update((formModel) => ({
            ...formModel,
            [formField]: formatted,
        }));
        const timeField =
            formField === "allowedTimeStart" ? this.quotaForm.allowedTimeStart() : this.quotaForm.allowedTimeEnd();
        timeField.markAsTouched();
    }

    private buildQuotaSummary(formModel: EditEventQuota.FormModel): string {
        const subjectLabel = EditEventQuota.isOrganizationQuotaType(formModel.quotaTypeItemId)
            ? "Each organization"
            : "Each user";
        const windowMode = EventQuota.normalizeWindowMode(formModel.windowMode);
        const limitParts: string[] = [];
        if (formModel.quantity) {
            const quantityNoun = formModel.countByEvent ? "event" : "occurrence";
            limitParts.push(`at most ${formModel.quantity} ${quantityNoun}${formModel.quantity === 1 ? "" : "s"}`);
        }
        if (!formModel.countByEvent) {
            const maxTimeLabel = EditEventQuota.formatMaxTimeLabel(formModel.maxTimeHours, formModel.maxTimeMinutes);
            if (maxTimeLabel) {
                limitParts.push(`at most ${maxTimeLabel} of booked time`);
            }
        }
        if (!limitParts.length) {
            return `${QUOTA_FORM_COPY.summaryIntro} set limits below to finish configuring this quota.`;
        }
        const limitsText = limitParts.length === 2 ? `${limitParts[0]} or ${limitParts[1]}` : limitParts[0];

        let periodPhrase: string;
        if (windowMode === "fixed") {
            periodPhrase = "during the fixed date range";
        } else if (windowMode === "today") {
            periodPhrase = "from today through the end date";
        } else {
            const periodCount = formModel.duration ?? 0;
            const periodUnit = durationUnitLabel(formModel.durationType, periodCount);
            const weekDefinition =
                formModel.durationType === "W" ? ` (${QUOTA_FORM_COPY.durationUnitWeekDefinition})` : "";
            periodPhrase = `within ${periodCount} ${periodUnit}${weekDefinition}`;
        }

        let periodNote: string;
        if (formModel.countByEvent) {
            periodNote = " Multiple occurrences on the same event count once toward the limit.";
        } else if (formModel.skipExisting) {
            periodNote = " Only occurrences on the current event count.";
        } else if (windowMode === "today" || windowMode === "future") {
            periodNote = " Only active and upcoming reservations count toward the limit.";
        } else if (windowMode === "fixed") {
            periodNote = " All reservations in the date range count toward the limit.";
        } else {
            periodNote = " Occurrences are counted in every sliding window of this length.";
        }

        const eventTypeNames = formModel.eventTypes?.map((eventType) => eventType.itemName).filter(Boolean) ?? [];
        const scopePhrase =
            eventTypeNames.length > 0
                ? ` for ${eventTypeNames.join(", ")} event type${eventTypeNames.length === 1 ? "" : "s"}`
                : "";

        return `${QUOTA_FORM_COPY.summaryIntro} ${subjectLabel} may create ${limitsText} ${periodPhrase}${scopePhrase} at the selected locations.${periodNote}`;
    }
}
